import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, thumbUrl } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useFavourites } from "@/contexts/FavouritesContext";
import { Heart } from "lucide-react";
import { toast } from "sonner";

function formatPrice(p, currency = "gbp") {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency.toUpperCase() }).format(p);
  } catch {
    return `£${Number(p).toFixed(2)}`;
  }
}

export default function SharedShortlist() {
  const { sid } = useParams();
  const { addItem, addMany, hasItem } = useCart();
  const { toggleFav, isFav, ids: favIds } = useFavourites();
  const [images, setImages] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/shortlists/${sid}`)
      .then((r) => api.get("/images", { params: { ids: r.data.image_ids.join(",") } }))
      .then((r) => setImages(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError("This shortlist doesn't exist or has expired."));
  }, [sid]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-6 mt-24 text-center" data-testid="shortlist-error">
        <p className="text-white/60">{error}</p>
        <Link to="/events" className="inline-block mt-8 border border-white/25 px-8 py-4 text-xs uppercase tracking-[0.3em] hover:border-[var(--pm-orange)]">
          Browse events
        </Link>
      </div>
    );
  }
  if (!images) {
    return <p className="text-white/50 text-sm py-24 text-center uppercase tracking-[0.3em]">Loading shortlist…</p>;
  }

  const notSaved = images.filter((im) => !favIds.includes(im.id));
  const notInBasket = images.filter((im) => !hasItem(im.id));

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 mt-8 pb-24" data-testid="shared-shortlist-page">
      <div className="mt-4 border-b border-white/10 pb-10 flex items-end justify-between flex-wrap gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-white/40">Shared with you</p>
          <h1 className="font-serif text-5xl md:text-7xl font-light leading-none mt-3">
            A <span className="pm-orange">shortlist</span>
          </h1>
          <p className="text-xs uppercase tracking-[0.2em] text-white/40 mt-6">
            {images.length} {images.length === 1 ? "photo" : "photos"} — someone thinks you'll like these
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {notSaved.length > 0 && (
            <button
              onClick={() => { notSaved.forEach((im) => toggleFav(im)); toast.success("Added to your favourites"); }}
              className="flex items-center gap-3 border border-white/25 px-6 py-4 text-xs uppercase tracking-[0.3em] hover:border-[var(--pm-orange)] transition"
              data-testid="shortlist-save-all"
            >
              <Heart size={15} /> Save all to my favourites
            </button>
          )}
          {notInBasket.length > 0 && (
            <button
              onClick={() => addMany(notInBasket)}
              className="bg-[var(--pm-orange)] text-black px-8 py-4 text-xs uppercase tracking-[0.3em] font-bold hover:opacity-90 transition"
              data-testid="shortlist-add-all"
            >
              Add all to basket ({notInBasket.length})
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-10">
        {images.map((img, idx) => (
          <div
            key={img.id}
            className="relative gallery-card block aspect-[4/5] fade-up"
            style={{ animationDelay: `${idx * 40}ms` }}
            data-testid={`shortlist-card-${img.id}`}
          >
            <Link to={`/image/${img.id}`} className="block w-full h-full">
              <img src={thumbUrl(img.id)} alt={img.title} loading="lazy" className="w-full h-full object-cover" />
              <div className="reveal">
                <div>
                  <h3 className="font-serif text-2xl text-white leading-tight">{img.title}</h3>
                  <p className="text-xs uppercase tracking-[0.2em] text-white/60 mt-2">{formatPrice(img.price, img.currency)}</p>
                </div>
                <span className="text-xs uppercase tracking-[0.2em] text-white border border-white/40 px-3 py-2">View</span>
              </div>
            </Link>
            <button
              onClick={(e) => { e.preventDefault(); toggleFav(img); }}
              className={`absolute top-3 left-3 z-10 p-2.5 transition ${
                isFav(img.id)
                  ? "bg-[var(--pm-orange)] text-black"
                  : "bg-black/70 text-white border border-white/40 hover:text-[var(--pm-orange)] hover:border-[var(--pm-orange)]"
              }`}
              data-testid={`shortlist-fav-${img.id}`}
              aria-label="Toggle favourite"
            >
              <Heart size={15} fill={isFav(img.id) ? "currentColor" : "none"} />
            </button>
            <button
              onClick={(e) => { e.preventDefault(); addItem(img); }}
              className={`absolute top-3 right-3 z-10 text-xs uppercase tracking-[0.2em] font-bold px-3 py-2 transition ${
                hasItem(img.id)
                  ? "bg-[var(--pm-orange)] text-black"
                  : "bg-black/70 text-white border border-white/40 hover:bg-[var(--pm-orange)] hover:text-black hover:border-[var(--pm-orange)]"
              }`}
              data-testid={`shortlist-add-to-cart-${img.id}`}
            >
              {hasItem(img.id) ? "✓ In basket" : "+ Basket"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
